/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, identity, none, RestSettings, SlicedDataSource } from '@gh/core-data';
import { Observable } from 'rxjs';
import { parametrizeUrl } from '@gh/core-util';
import {
  EmailCampaignAccountInfoDto,
  EmailCampaignDetailsDto,
  EmailCampaignFilterDto,
  EmailCampaignListItemDto,
  EmailCampaignRecipientDto,
  EmailCampaignRecipientFilterDto,
  EmailCampaignReportDto,
  EmailCampaignTemplateDto,
} from './email-campaigns.dto';
import {
  EmailCampaignDetailsMapper,
  EmailCampaignListItemMapper,
  EmailCampaignRecipientFilterMapper,
  EmailCampaignReportMapper,
} from './email-campaigns.mapper';

@Injectable()
export class EmailCampaignRestService extends AbstractRestService {
  constructor(settings: RestSettings) {
    super(settings);
  }

  findAllAsDataSource(filter: EmailCampaignFilterDto): SlicedDataSource<EmailCampaignListItemDto> {
    return this.createSlicedDataSourceGet(`${this.bp}/email-campaigns`, filter, identity(), arrayOf(EmailCampaignListItemMapper));
  }

  getReport(campaignId: ID): Observable<EmailCampaignReportDto> {
    return this.httpGet(`${this.bp}/email-campaigns/${campaignId}/report`, EmailCampaignReportMapper);
  }

  get(campaignId: ID): Observable<EmailCampaignDetailsDto> {
    return this.httpGet(`${this.bp}/email-campaigns/${campaignId}`, EmailCampaignDetailsMapper);
  }

  create(campaign: EmailCampaignDetailsDto): Observable<EmailCampaignDetailsDto> {
    return this.httpPost(`${this.bp}/email-campaigns`, campaign, EmailCampaignDetailsMapper);
  }

  update(campaign: EmailCampaignDetailsDto): Observable<EmailCampaignDetailsDto> {
    return this.httpPut(`${this.bp}/email-campaigns/${campaign.campaignId}`, campaign, EmailCampaignDetailsMapper);
  }

  remove(campaignId: ID, version: EntityVersion, markAsDeleted: boolean = false): Observable<void> {
    return this.httpDelete(
      parametrizeUrl(`${this.bp}/email-campaigns/${campaignId}`, { version, markAsDeleted }),
      void 0,
      identity(),
      none());
  }

  findAllRecipients(filter: EmailCampaignRecipientFilterDto): Observable<EmailCampaignRecipientDto[]> {
    return this.httpPut(`${this.bp}/email-campaigns/recipients`, filter, EmailCampaignRecipientFilterMapper, arrayOf(identity()));
  }

  findAllTemplates(): Observable<EmailCampaignTemplateDto[]> {
    return this.httpGet(`${this.bp}/email-campaigns/templates`, identity());
  }

  getAccountInfo(): Observable<EmailCampaignAccountInfoDto> {
    return this.httpGet(`${this.bp}/email-campaigns/account-info`, identity());
  }
}
